import { CommandCenter } from "apollo/components/command-center";
// Registers its router listeners at app start, before any Back needs them.
import "apollo/lib/in-app-history";
import { ContentEditDialog } from "apollo/components/content-edit-dialog";
import { ContentEditDialogProvider } from "apollo/components/content-edit-dialog/container";
import {
  TabBarHeightProbe,
  TabBarProvider,
  useTabBarHidden,
} from "apollo/components/mobile/tab-bar-context";
import { MobileTabBarPlaceholder } from "apollo/components/mobile/tab-bar-frame";
import { Header, MobileHeader } from "apollo/components/top-navigation";
import { usePageTitle } from "apollo/lib/page-title";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { ConfirmModal } from "ui/components/confirm-modal";
import { useSession } from "ui/containers/session";
import { cn } from "ui/lib/utils";
import ErrorBoundary from "../error-boundary";

const MobileTabBar = dynamic(
  () =>
    import("apollo/components/mobile/tab-bar").then((mod) => mod.MobileTabBar),
  { ssr: false, loading: () => <MobileTabBarPlaceholder /> }
);

// --tab-bar-height is the space the phone tab bar covers at the bottom of the
// screen (0 on desktop or while a screen hides the bar). Pages sized to the
// viewport subtract it.
const Shell = ({ children }) => {
  const tabBarHidden = useTabBarHidden();
  return (
    <div
      id="default-layout"
      className={cn(
        "[--tab-bar-height:calc(3.5rem+env(safe-area-inset-bottom))] lg:[--tab-bar-height:0px]",
        tabBarHidden && "[--tab-bar-height:0px]"
      )}
    >
      <TabBarHeightProbe />
      {children}
    </div>
  );
};

export const LayoutDefault = ({ children }) => {
  const { session, hasAccessedSession } = useSession();
  const { push, pathname } = useRouter();
  const title = usePageTitle();

  useEffect(() => {
    if (!!session || !process?.browser || !hasAccessedSession) {
      return;
    }
    push("/", "/");
  }, [session, push, hasAccessedSession]);

  // if (!session) {
  //   return <LoadingOverlay />;
  // }

  return (
    <ErrorBoundary>
      <Head>
        <title>{title}</title>
      </Head>
      <ContentEditDialogProvider>
        <TabBarProvider>
          <Shell>
            <a
              href="#content"
              onClick={(event) => {
                event.preventDefault();
                document.getElementById("content")?.focus();
              }}
              className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[100] focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-black"
            >
              Skip to content
            </a>
            <div className="bg-background">
              <div className="w-full pb-[var(--tab-bar-height)]">
                <div id="top-navigation" className="sticky top-0 z-50">
                  <Header />
                  <MobileHeader />
                </div>
                <main id="content" tabIndex={-1} className="outline-none">
                  {children}
                </main>
              </div>
            </div>
            <MobileTabBar />
            <ContentEditDialog />
            <CommandCenter />
            <ConfirmModal />
          </Shell>
        </TabBarProvider>
      </ContentEditDialogProvider>
    </ErrorBoundary>
  );
};
